import NiceModal from '@ebay/nice-modal-react'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import ClearOutlined from '@mui/icons-material/ClearOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material'
import { ErrorReasons } from '@shared/constants/errors.constants'
import {
  ChangePasswordBody,
  changePasswordBodySchema
} from '@shared/dto/auth.dto'
import {
  ConfigInfoForm,
  UpdateUserForm,
  updateUserFormSchema
} from '@shared/dto/users.dto'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import TimezoneSelect from 'react-timezone-select'
import ContainedAvatar from '../../components/ContainedAvatar'
import { PasswordInput } from '../../components/forms/PasswordInput'
import LanguageSelector from '../../components/LanguageSelector'
import { LoaderButton } from '../../components/LoaderButton'
import { DeleteAccountModal } from '../../components/modals/DeleteAccountModal'
import SubHeaderProfile from '../../components/SubHeaderProfile'
import { useAuthStore } from '../../hooks/useAuthStore'
import { useChangePasswordMutation } from '../../hooks/useMutations/useAuthMutations'
import { useUserUpdateMutation } from '../../hooks/useMutations/useUsersMutations'
import { useUser } from '../../hooks/useQueries/useUsers'
import { useSnackbar } from '../../hooks/useSnackbar'
import { hasCombination, hasMinLength } from '../../utils/validation.utils'

type PasswordIconType = 'success' | 'info' | 'error'

const iconSx = {
  background: 'text.secondary',
  '& .MuiFilledInput-root': {
    '&:before': { borderBottom: 'none' },
    '&:after': { borderBottom: 'none' },
    '&:hover:before': { borderBottom: 'none' }
  }
}

export default function Profile() {
  const theme = useTheme()
  const { t } = useTranslation()
  const { accessToken } = useAuthStore()
  const [hasCheckedDeleteAccount, setHasCheckedDeleteAccount] = useState(false)
  const { userId } = useAuthStore() as { userId: string }
  const { data: userData, isLoading, isFetching } = useUser(userId)
  const avatarName = `${userData?.name?.charAt(0) || ''}${userData?.lastName?.charAt(0) || ''}`
  const userUpdateMutation = useUserUpdateMutation()
  const changePasswordMutation = useChangePasswordMutation()
  const { showSnackbar } = useSnackbar()

  const [passwordStatusIcons, setPasswordStatusIcons] = useState<{
    match: PasswordIconType
    length: PasswordIconType
    combination: PasswordIconType
  }>({
    match: 'info',
    length: 'info',
    combination: 'info'
  })
  const [alertData, setAlertData] = useState<{
    title: string
    description: string
  } | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty }
  } = useForm<UpdateUserForm>({
    defaultValues: {
      name: '',
      lastName: '',
      avatarUri: '',
      phone: '',
      timezone: ''
    },
    resolver: standardSchemaResolver(updateUserFormSchema),
    mode: 'onBlur'
  })

  const {
    register,
    handleSubmit: handleSubmitPassword,
    formState: { errors, isDirty: isPasswordDirty },
    watch: watchPassword,
    reset: resetPassword
  } = useForm<ChangePasswordBody & { repeatPassword: string }>({
    defaultValues: {
      accessToken: accessToken ?? '',
      oldPassword: '',
      newPassword: ''
    },
    resolver: standardSchemaResolver(changePasswordBodySchema)
  })

  useEffect(() => {
    if (userData) {
      reset({
        name: userData.name,
        lastName: userData.lastName,
        avatarUri: userData.avatarUri,
        phone: userData.phone || '',
        timezone:
          userData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      })
    }
  }, [userData, reset])

  useEffect(() => {
    resetPassword()
  }, [resetPassword])

  const oldPassword = watchPassword('oldPassword')
  const newPassword = watchPassword('newPassword')
  const repeatPassword = watchPassword('repeatPassword')

  useEffect(() => {
    if (!oldPassword && !newPassword && !repeatPassword) {
      setPasswordStatusIcons({
        match: 'info',
        length: 'info',
        combination: 'info'
      })
      return
    }

    setPasswordStatusIcons({
      match:
        newPassword && newPassword !== '' && newPassword === repeatPassword
          ? 'success'
          : 'error',
      length:
        hasMinLength(newPassword || '') && hasMinLength(repeatPassword || '')
          ? 'success'
          : 'error',
      combination:
        hasCombination(newPassword || '') &&
        hasCombination(repeatPassword || '')
          ? 'success'
          : 'error'
    })
  }, [oldPassword, newPassword, repeatPassword])

  const onSubmit = (data: ConfigInfoForm) => {
    userUpdateMutation.mutate(
      {
        userId,
        user: {
          name: userData?.name ?? '',
          lastName: userData?.lastName ?? '',
          phone: userData?.phone ?? '',
          timezone: userData?.timezone ?? '',
          avatarUri: userData?.avatarUri ?? '',
          ...data
        }
      },
      {
        onSuccess: () =>
          showSnackbar({
            message: t('user.update_success'),
            severity: 'success'
          }),
        onError: () =>
          showSnackbar({ message: t('user.update_error'), severity: 'error' })
      }
    )
  }

  const isPasswordDisabled =
    !oldPassword ||
    !newPassword ||
    !repeatPassword ||
    Object.values(passwordStatusIcons).some(status => status !== 'success') ||
    changePasswordMutation.isPending

  const onSubmitPassword = (data: ChangePasswordBody) => {
    changePasswordMutation.mutate(
      {
        accessToken: accessToken ?? '',
        oldPassword: data.oldPassword,
        newPassword: data.newPassword
      },
      {
        onSuccess: () => {
          showSnackbar({
            message: t('user.password_updated'),
            severity: 'success'
          })
          resetPassword()
        },
        onError: (error: any) => {
          if (error.response.data.data.reason === ErrorReasons.LIMIT_EXCEEDED) {
            setAlertData({
              title: t('security.alert_title_retries'),
              description: t('security.alert_subtitle_retries')
            })
          } else {
            setAlertData({
              title: t('security.alert_title'),
              description: t('security.alert_subtitle')
            })
          }
        }
      }
    )
  }

  const handleCombinedSubmit = () => {
    if (isDirty && isPasswordDirty) {
      handleSubmit(onSubmit)()
      handleSubmitPassword(onSubmitPassword)()
    } else if (isDirty) {
      handleSubmit(onSubmit)()
    } else if (isPasswordDirty) {
      handleSubmitPassword(onSubmitPassword)()
    }
  }

  const renderValidationIcon = useCallback(
    (type: PasswordIconType) =>
      type === 'success' ? (
        <CheckCircleOutlinedIcon color='success' />
      ) : (
        <InfoOutlinedIcon color={type} />
      ),
    []
  )

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result as string
      setValue('avatarUri', base64, { shouldDirty: true })
    }
    reader.readAsDataURL(file)
  }

  const handleTimezoneChange = (timezone: any) => {
    setValue(
      'timezone',
      typeof timezone === 'string' ? timezone : timezone.value,
      { shouldDirty: true }
    )
  }

  const avatarUri = watch('avatarUri')

  if (isLoading || isFetching || !userId) {
    return (
      <>
        <Box
          sx={{
            backgroundColor: 'background.default',
            width: '100%',
            minHeight: '4.5rem',
            display: 'flex',
            alignItems: 'center',
            mb: '3rem'
          }}
        >
          <Container maxWidth='xl' disableGutters>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{ width: '100%' }}
            >
              <Skeleton
                variant='text'
                width='18.75rem'
                height='1.25rem'
                sx={{ bgcolor: 'rgba(255,255,255,0.3)' }}
              />
              <Skeleton
                variant='rectangular'
                width='5rem'
                height='2.25rem'
                sx={{ bgcolor: 'rgba(255,255,255,0.3)' }}
              />
            </Stack>
          </Container>
        </Box>

        <Container maxWidth='xl' disableGutters>
          <Grid container justifyContent='center'>
            <Grid size={{ xs: 12, md: 8 }} marginTop={'3rem'}>
              <Box display='flex' justifyContent='flex-end'>
                <Skeleton variant='rectangular' width='7.5rem' height='2rem' />
              </Box>

              <Box display='flex' justifyContent='space-between' py={'1.5rem'}>
                <Box display='flex' flexDirection='column' gap='1rem'>
                  <Skeleton variant='text' width='9.375rem' height='2rem' />
                  <Skeleton variant='text' width='15.625rem' height='1.5rem' />
                </Box>
                <Box
                  display='flex'
                  flexDirection='row'
                  gap='1.5rem'
                  width='50%'
                >
                  <Skeleton variant='circular' width='7.5rem' height='7.5rem' />
                  <Box
                    display='flex'
                    flexDirection='column'
                    gap='0.5rem'
                    alignItems='flex-start'
                  >
                    <Skeleton
                      variant='rectangular'
                      width='7.5rem'
                      height='2rem'
                    />
                    <Skeleton variant='text' width='12.5rem' height='1rem' />
                    <Skeleton
                      variant='rectangular'
                      width='5rem'
                      height='1.5rem'
                    />
                  </Box>
                </Box>
              </Box>
              <Divider />

              <Box display='flex' justifyContent='space-between' py={'2rem'}>
                <Box display='flex' flexDirection='column' gap='1rem'>
                  <Skeleton variant='text' width='12.5rem' height='2rem' />
                  <Skeleton variant='text' width='18.75rem' height='1.5rem' />
                </Box>
                <Box display='flex' flexDirection='column' gap={2} width='50%'>
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                </Box>
              </Box>
              <Divider />

              <Box display='flex' justifyContent='space-between' py={'2rem'}>
                <Box display='flex' flexDirection='column' gap='1rem'>
                  <Skeleton variant='text' width='11.25rem' height='2rem' />
                  <Skeleton variant='text' width='17.5rem' height='1.5rem' />
                </Box>
                <Box display='flex' flexDirection='column' gap={2} width='50%'>
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                </Box>
              </Box>
              <Divider />

              <Box display='flex' justifyContent='space-between' py={'2rem'}>
                <Box display='flex' flexDirection='column' gap='1rem'>
                  <Skeleton variant='text' width='10rem' height='2rem' />
                  <Skeleton variant='text' width='16.25rem' height='1.5rem' />
                </Box>
                <Box display='flex' flexDirection='column' gap={2} width='50%'>
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height='3.5rem'
                  />
                </Box>
              </Box>
              <Divider />

              <Box display='flex' justifyContent='space-between' py={'2rem'}>
                <Box display='flex' flexDirection='column' gap='1rem'>
                  <Skeleton variant='text' width='12.5rem' height='2rem' />
                  <Skeleton variant='text' width='18.75rem' height='1.5rem' />
                </Box>
                <Box display='flex' flexDirection='column' gap={2} width='50%'>
                  <Skeleton variant='text' width='100%' height='1.5rem' />
                  <Skeleton
                    variant='rectangular'
                    width='12.5rem'
                    height='1.5rem'
                  />
                  <Skeleton
                    variant='rectangular'
                    width='7.5rem'
                    height='2rem'
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </>
    )
  }

  return (
    <>
      <SubHeaderProfile
        title={t('user.my_account')}
        subtitle={t('user.edit_my_profile')}
        isDirty={isDirty}
      />
      <Container maxWidth='xl' disableGutters>
        <Grid container justifyContent='center'>
          <Grid size={{ xs: 12, md: 10 }} marginTop={'3rem'}>
            <Box display='flex' justifyContent='flex-end'>
              <LoaderButton
                variant='contained'
                onClick={handleCombinedSubmit}
                type='submit'
                sx={{
                  padding: '0.25rem 0.75rem',
                  fontSize: '0.75rem',
                  width: 'fit-content',
                  textTransform: 'capitalize',
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText'
                }}
                label={t('user.save_changes')}
                disabled={
                  userUpdateMutation.isPending ||
                  changePasswordMutation.isPending ||
                  (!isDirty && !isPasswordDirty) ||
                  (isPasswordDirty && isPasswordDisabled)
                }
                loading={userUpdateMutation.isPending}
              />
            </Box>

            <Box display='flex' justifyContent='space-between' py={'1.5rem'}>
              <Box display='flex' flexDirection='column' gap='1rem'>
                <Typography variant='h6' fontWeight='bold'>
                  {t('profile.image')}
                </Typography>
                <Typography variant='body1'>
                  {t('profile.image_subtitle')}
                </Typography>
              </Box>
              <Box display='flex' flexDirection='row' gap='1.5rem' width='40%'>
                {avatarUri ? (
                  <ContainedAvatar
                    src={avatarUri}
                    sx={{ width: '4.5rem', height: '4.5rem' }}
                  />
                ) : (
                  <Avatar
                    variant='square'
                    sx={{ width: '4.5rem', height: '4.5rem' }}
                  >
                    {avatarName}
                  </Avatar>
                )}
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='flex-start'
                  justifyContent='center'
                  height='4.5rem'
                >
                  <Button
                    component='label'
                    variant='text'
                    startIcon={<UploadFileOutlinedIcon />}
                    sx={{
                      textTransform: 'capitalize'
                    }}
                  >
                    {t('user.change_image')}
                    <input
                      type='file'
                      accept='image/*'
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                  <Typography
                    variant='caption'
                    sx={{ paddingLeft: '0.6rem', color: 'text.secondary' }}
                  >
                    {t('profile.change_image')}
                  </Typography>
                  <Button
                    variant='text'
                    size='small'
                    onClick={() => {
                      setValue('avatarUri', '', { shouldDirty: true })
                    }}
                    disabled={!avatarUri}
                    startIcon={<ClearOutlined />}
                    sx={{
                      color: 'secondary.main',
                      textTransform: 'none',
                      fontSize: '0.81rem'
                    }}
                  >
                    {t('common.delete')}
                  </Button>
                </Box>
              </Box>
            </Box>
            <Divider />

            <Box display='flex' justifyContent='space-between' py={'2rem'}>
              <Box display='flex' flexDirection='column' gap='1rem'>
                <Typography variant='h6' fontWeight='bold'>
                  {t('profile.personal_info')}
                </Typography>
                <Typography variant='body1'>
                  {t('profile.personal_description')}
                </Typography>
              </Box>
              <Box
                display='flex'
                flexDirection='column'
                gap={'1.5rem'}
                width='40%'
              >
                <Controller
                  name='name'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder={t('user.first_name')}
                      label={t('user.first_name')}
                      variant='filled'
                      fullWidth
                      sx={{
                        background: 'text.secondary',
                        '& .MuiFilledInput-root': {
                          '&:before': { borderBottom: 'none' }
                        }
                      }}
                    />
                  )}
                />
                <Controller
                  name='lastName'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder={t('user.last_name')}
                      label={t('user.last_name')}
                      variant='filled'
                      fullWidth
                      sx={{
                        background: 'text.secondary',
                        '& .MuiFilledInput-root': {
                          '&:before': { borderBottom: 'none' }
                        }
                      }}
                    />
                  )}
                />
                <Controller
                  name='phone'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder={t('user.contact_phone')}
                      label={t('user.contact_phone')}
                      variant='filled'
                      fullWidth
                      sx={{
                        background: 'text.secondary',
                        '& .MuiFilledInput-root': {
                          '&:before': { borderBottom: 'none' }
                        }
                      }}
                    />
                  )}
                />
                <TextField
                  name='example_email'
                  value={userData?.email}
                  label={t('user.corporative_email')}
                  variant='filled'
                  fullWidth
                  disabled
                  sx={{
                    background: 'text.secondary',
                    '& .MuiFilledInput-root': {
                      '&:before': { borderBottom: 'none' }
                    },
                    '& .MuiFilledInput-root.Mui-disabled': {
                      '&:before': { borderBottom: 'none' }
                    }
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <Tooltip title={t('profile.tooltip_email')}>
                          <InputAdornment position='end'>
                            <InfoOutlinedIcon />
                          </InputAdornment>
                        </Tooltip>
                      )
                    }
                  }}
                />
              </Box>
            </Box>
            <Divider />

            <Box display='flex' justifyContent='space-between' py={'2rem'}>
              <Box display='flex' flexDirection='column' gap='1rem'>
                <Typography variant='h6' fontWeight='bold'>
                  {t('profile.configuration')}
                </Typography>
                <Typography variant='body1'>
                  {t('profile.configuration_description')}
                </Typography>
              </Box>
              <Box
                display='flex'
                flexDirection='column'
                gap={'1.5rem'}
                width='40%'
              >
                <Box>
                  <Typography variant='body2' mb={1}>
                    {t('user.change_language')}
                  </Typography>
                  <LanguageSelector />
                </Box>
                <Controller
                  name='timezone'
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <Typography variant='body2' mb={1}>
                        {t('user.timezone')}
                      </Typography>
                      <TimezoneSelect
                        value={field.value || ''}
                        onChange={handleTimezoneChange}
                        styles={{
                          control: provided => ({
                            ...provided,
                            minHeight: '3.5rem',
                            height: '3.5rem',
                            cursor: 'pointer',
                            backgroundColor: '#ffffff17',
                            border: 'none',
                            borderRadius: '4px',
                            boxShadow: 'none',
                            '&:hover': {
                              border: 'none',
                              boxShadow: 'none'
                            }
                          }),
                          menu: provided => ({
                            ...provided,
                            zIndex: 9999,
                            backgroundColor: theme.palette.background.default,
                            border: `1px solid ${theme.palette.background.default}`
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected
                              ? theme.palette.action.selected
                              : 'transparent',
                            color: state.isSelected
                              ? theme.palette.text.primary
                              : theme.palette.text.secondary,
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover
                            }
                          }),
                          singleValue: provided => ({
                            ...provided,
                            color: theme.palette.text.primary
                          })
                        }}
                      />
                    </Box>
                  )}
                />
              </Box>
            </Box>
            <Divider />

            <Box display='flex' justifyContent='space-between' py={'2rem'}>
              <Box display='flex' flexDirection='column' gap='1rem'>
                <Typography variant='h6' fontWeight='bold'>
                  {t('profile.security')}
                </Typography>
                <Typography variant='body1'>
                  {t('profile.security_description')}
                </Typography>
                <Alert
                  severity='error'
                  sx={{
                    visibility: changePasswordMutation.isError
                      ? 'visible'
                      : 'hidden'
                  }}
                >
                  <AlertTitle>{alertData?.title || ''}</AlertTitle>
                  {alertData?.description || ''}
                </Alert>
              </Box>
              <Box
                display='flex'
                flexDirection='column'
                gap={'1.5rem'}
                width='40%'
              >
                <PasswordInput
                  name='oldPassword'
                  register={register}
                  errors={errors}
                  label={t('user.current_password')}
                  sx={iconSx}
                />

                <PasswordInput
                  name='newPassword'
                  register={register}
                  errors={errors}
                  label={t('user.new_password')}
                  sx={iconSx}
                />

                <PasswordInput
                  name='repeatPassword'
                  register={register}
                  errors={errors}
                  label={t('user.repeat_password')}
                  sx={iconSx}
                />
                {[
                  ['match', 'password.match'],
                  ['length', 'password.length'],
                  ['combination', 'password.combination']
                ].map(([key, label]) => (
                  <Box
                    key={key}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {renderValidationIcon(
                      passwordStatusIcons[
                        key as keyof typeof passwordStatusIcons
                      ]
                    )}
                    <Typography>{t(label)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Divider />

            <Box display='flex' justifyContent='space-between' py={'2rem'}>
              <Box display='flex' flexDirection='column' gap='1rem'>
                <Typography
                  variant='h6'
                  fontWeight='bold'
                  fontFamily={'Noto Sans'}
                >
                  {t('profile.delete_account')}
                </Typography>
                <Typography variant='body1' fontFamily={'Noto Sans'}>
                  {t('profile.delete_account_subtitle')}
                </Typography>
              </Box>

              <Box display='flex' flexDirection='column' gap={2} width='40%'>
                <Stack spacing={2}>
                  <Typography variant='body2' fontWeight='bold'>
                    {t('profile.delete_account_description')}
                  </Typography>
                  <FormControlLabel
                    sx={{ marginTop: '1rem' }}
                    control={
                      <Checkbox
                        checked={hasCheckedDeleteAccount}
                        onChange={e =>
                          setHasCheckedDeleteAccount(e.target.checked)
                        }
                      />
                    }
                    label={t('profile.delete_account_check')}
                  />
                  <Button
                    onClick={() => NiceModal.show(DeleteAccountModal)}
                    size='small'
                    sx={{ width: 'fit-content' }}
                    variant='contained'
                    disabled={!hasCheckedDeleteAccount}
                    startIcon={<DeleteOutlinedIcon />}
                    color='error'
                  >
                    {t('user.delete_button')}
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  )
}
